import {
  isSensitiveCapabilityPath,
  sanitizeTelemetryPath,
} from "@/lib/analytics/privacy";

/**
 * Pure logic for the first-touch attribution capture.
 *
 * Extracted from `app/_components/first-touch-capture.tsx` so the
 * snapshot-building, UTM filtering, referrer parsing, and PostHog
 * property shaping can be unit-tested without rendering the React
 * component (project doesn't ship jsdom — see
 * `apps/web/src/lib/intake/__tests__/status-token-storage.test.ts`
 * for the convention).
 */

export const FIRST_TOUCH_STORAGE_KEY = "gp_first_touch";

export const NON_MARKETING_PREFIXES: readonly string[] = [
  "/portal",
  "/sign-in",
  "/sign-up",
  "/auth",
  "/leads",
  "/projects",
  "/clients",
  "/internal",
  "/dev",
  "/api",
  "/scout",
];

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export type FirstTouch = {
  landed_at_iso: string;
  landing_path: string;
  landing_referrer_host?: string;
} & Partial<Record<UtmKey, string>>;

/** Defensive cap on UTM value length — most ad networks use < 100 chars. */
export const UTM_MAX_LENGTH = 200;

export function isMarketingRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  if (isSensitiveCapabilityPath(pathname)) return false;
  // Path-segment boundary check: `/portal` matches `/portal` and
  // `/portal/anything`, but NOT `/portalish` or `/portal-update`.
  // cubic on PR #417 round 9 caught the prefix over-match: a future
  // marketing page like `/portfolio` or `/leadership` would have been
  // misclassified as non-marketing, silently disabling first-touch
  // capture + Instantly + marketing telemetry on those pages.
  return !NON_MARKETING_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

/**
 * Build a first-touch snapshot from raw inputs. Pure: no
 * `window` or `document` reads — the component layer is responsible
 * for sourcing pathname/search/referrer/now.
 */
export function buildFirstTouchSnapshot(args: {
  pathname: string;
  search: string; // raw search string, may include leading `?`
  referrer: string;
  currentOrigin: string;
  nowIso: string;
}): FirstTouch {
  const params = new URLSearchParams(args.search.startsWith("?") ? args.search.slice(1) : args.search);
  const utm: Partial<Record<UtmKey, string>> = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value.slice(0, UTM_MAX_LENGTH);
  }

  let referrerHost: string | undefined;
  if (args.referrer) {
    try {
      const url = new URL(args.referrer);
      // Skip same-origin referrers — internal navigations, not
      // acquisition signals.
      if (url.host !== new URL(args.currentOrigin).host) {
        referrerHost = url.host;
      }
    } catch {
      // Malformed referrer — ignore.
    }
  }

  return {
    landed_at_iso: args.nowIso,
    // Cap at write time: link-wrappers/spam crawlers can mint pathological
    // paths; an uncapped value would fail the server's 500-char bound and
    // (pre per-field-catch) discarded the whole snapshot's attribution.
    landing_path: sanitizeTelemetryPath(args.pathname),
    ...(referrerHost ? { landing_referrer_host: referrerHost.slice(0, UTM_MAX_LENGTH) } : {}),
    ...utm,
  };
}

/**
 * Allowed key set on a FirstTouch object — anything outside this set
 * is rejected by `isValidFirstTouch`. Without this, a tampered
 * localStorage entry could smuggle arbitrary key/value pairs through
 * to PostHog via `toPostHogProperties` (which iterates `Object.entries`
 * and forwards every string property).
 */
const ALLOWED_FIRST_TOUCH_KEYS: ReadonlySet<string> = new Set<string>([
  "landed_at_iso",
  "landing_path",
  "landing_referrer_host",
  ...UTM_KEYS,
]);

/**
 * Validate a value parsed from localStorage as a FirstTouch. Returns
 * true only if:
 *   - it's an object
 *   - both required keys (`landed_at_iso`, `landing_path`) are strings
 *   - every other key is in the allowlist AND its value is a string
 *
 * Defensive against tampered storage values (someone editing
 * localStorage in DevTools, or a stale shape from a prior schema
 * version).
 *
 * cubic 2026-05-09 P2: prior version accepted any extra keys,
 * which `toPostHogProperties` then forwarded with a `first_touch_`
 * prefix — letting an attacker pollute PostHog person properties
 * with arbitrary names by editing localStorage.
 */
export function isValidFirstTouch(value: unknown): value is FirstTouch {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.landed_at_iso !== "string") return false;
  if (typeof v.landing_path !== "string") return false;
  for (const [key, val] of Object.entries(v)) {
    if (!ALLOWED_FIRST_TOUCH_KEYS.has(key)) return false;
    if (typeof val !== "string") return false;
  }
  return true;
}

/**
 * Read + validate the stored first-touch snapshot. Returns null on any
 * missing/tampered/legacy-shaped value — attribution is best-effort and
 * must never throw into a submit path. Takes the storage as a parameter
 * so the pure-logic tests don't need jsdom.
 */
export function readStoredFirstTouch(
  storage: Pick<Storage, "getItem">,
): FirstTouch | null {
  try {
    const raw = storage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidFirstTouch(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Format a snapshot into the property bag we hand to PostHog
 * `set_once` / `register_once`. Every key is prefixed with
 * `first_touch_` so dashboards can filter cleanly without colliding
 * with last-touch / session properties PostHog auto-captures.
 */
export function toPostHogProperties(snapshot: FirstTouch): Record<string, string> {
  const properties: Record<string, string> = {};
  for (const [k, v] of Object.entries(snapshot)) {
    if (typeof v === "string") properties[`first_touch_${k}`] = v;
  }
  return properties;
}
